import React, { useEffect, useRef } from 'react';
import { X, Calendar, Users, MapPin, Shield } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Premium centered modal shown BEFORE the affiliate redirect.
 * Title: "Your Trip Summary"
 * Truthful supplier handoff: no unverified totals, cancellation promises or
 * reservation claims. The partner confirms payable rates before checkout.
 */
export default function CheckoutPreviewModal({
  isOpen,
  onClose,
  hotel,
  params = {},
  onConfirm,
}) {
  const { t, language } = useLanguage();
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !hotel) return undefined;
    const previouslyFocused = document.activeElement;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || []).filter((el) => el.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = priorOverflow;
      if (previouslyFocused?.isConnected) previouslyFocused.focus?.();
    };
  }, [isOpen, hotel, onClose]);

  if (!isOpen || !hotel) return null;

  const calculateNights = () => {
    if (!params.checkin || !params.checkout) return 1;
    const checkin = new Date(params.checkin);
    const checkout = new Date(params.checkout);
    const nights = Math.ceil((checkout - checkin) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 1;
  };

  const nights = calculateNights();
  // A search card can carry a per-night estimate, a full-stay quote, or
  // an illustrative AI value. Do NOT manufacture a payment total by
  // multiplying unknown price semantics by nights.
  const displayedPrice = typeof hotel.price === 'string' && hotel.price.trim()
    ? hotel.price
    : null;
  const priceMayBeEstimated = hotel.estimated || hotel.priceAiEstimated
    || hotel.inventorySource === 'ai_estimate' || hotel.inventorySource === 'redirect_only'
    || hotel.inventorySource === 'hotelbeds' || hotel.priceBasis === 'unconfirmed';

  const formatDate = (d) => {
    if (!d) return '—';
    const locale = language === 'bg' ? 'bg-BG' : 'en-US';
    // ISO date-only strings must stay on the selected calendar day worldwide.
    return new Date(`${d}T12:00:00`).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  };


  return (
    <div
      data-testid="checkout-preview-modal"
      role="presentation"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-preview-title"
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — deep navy tone to match RedirectOverlay for a
            consistent premium booking experience across the flow. */}
        <div className="relative px-6 pt-5 pb-4 bg-gradient-to-br from-gray-900 via-gray-900 to-blue-950 text-white">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-cyan-300">
            <Shield className="w-3 h-3" />
            {t('booking.reviewBeforeConfirm')}
          </div>
          <h2 id="checkout-preview-title" className="text-xl sm:text-2xl font-extrabold mt-1 tracking-tight">{t('booking.tripSummary')}</h2>
          <button
            data-testid="modal-close-btn"
            type="button"
            ref={closeButtonRef}
            onClick={onClose}
            aria-label={t('common.close')}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition text-white/80 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Hotel snapshot */}
        <div className="px-6 pt-5 pb-4 flex gap-3">
          {hotel.image && (
            <img
              src={hotel.image}
              alt={hotel.name}
              className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 leading-tight truncate">{hotel.name}</h3>
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1 truncate">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{hotel.location || params.city}</span>
            </p>
            {Number.isFinite(Number(hotel.rating))
              && Number(hotel.rating) > 0
              && [5, 10].includes(Number(hotel.ratingScale))
              && Number(hotel.rating) <= Number(hotel.ratingScale) && (
              <div className="flex items-center gap-1 mt-1 text-yellow-600 text-xs">
                <span aria-hidden="true">★</span>
                <span className="text-gray-600">{Number(hotel.rating).toFixed(1)}/{hotel.ratingScale}</span>
              </div>
            )}
          </div>
        </div>

        {/* Trip detail rows */}
        <div className="px-6 py-4 mx-6 mb-4 bg-gray-50 rounded-2xl space-y-3">
          <Row icon={<Calendar className="w-4 h-4" />} label={t('common.checkin')} value={formatDate(params.checkin)} />
          <Row icon={<Calendar className="w-4 h-4" />} label={t('common.checkout')} value={formatDate(params.checkout)} />
          <Row
            icon={<Users className="w-4 h-4" />}
            label={t('common.guests')}
            value={`${params.guests || 2} ${language === 'bg' ? 'възрастни' : 'adults'}${params.children > 0 ? ` · ${params.children} ${language === 'bg' ? 'деца' : 'children'}` : ''} · ${params.rooms || 1} ${(params.rooms || 1) > 1 ? t('common.rooms') : t('common.room')}`}
          />
          <div className="border-t border-gray-200 pt-3 flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {nights} {nights !== 1 ? t('common.nights') : t('common.night')}
            </span>
            <span className="text-gray-700 text-right">
              {displayedPrice || (language === 'bg' ? 'Цена при партньора' : 'Price at partner')}
            </span>
          </div>
        </div>

        {/* The partner, not TravelAIBG, confirms the payable price and conditions. */}
        <div className="px-6 pb-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-base font-semibold text-gray-900">
              {language === 'bg' ? 'Показана цена' : 'Displayed price'}
            </span>
            <span className="text-xl sm:text-2xl font-extrabold text-blue-600 text-right">
              {displayedPrice || (language === 'bg' ? 'Проверете при партньора' : 'Check with partner')}
            </span>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2" role="note">
            <div className="flex items-center gap-2 text-blue-900 font-semibold text-sm">
              <Shield className="w-4 h-4" />
              {language === 'bg' ? 'Преглед преди резервация' : 'Review before booking'}
            </div>
            <p className="text-sm text-blue-900">
              {language === 'bg'
                ? 'Ще бъдете пренасочени към доставчика. Там проверете окончателната цена, свободните места, таксите и условията за анулация, преди да платите.'
                : 'You will continue to the provider. Verify the final price, availability, fees and cancellation terms there before paying.'}
            </p>
            {hotel.inventorySource === 'hotelbeds' && (
              <p className="text-xs text-amber-800 font-medium">
                {language === 'bg'
                  ? 'Данните са от хотелски доставчик, но тук няма потвърдена крайна потребителска цена или гаранция за наличност при външния партньор.'
                  : 'Supplier inventory is not a confirmed retail quote or a guarantee that this hotel is offered by the external partner.'}
              </p>
            )}
            {priceMayBeEstimated && (
              <p className="text-xs text-amber-800 font-medium">
                {language === 'bg'
                  ? 'Тази цена е ориентировъчна, а не потвърдена оферта.'
                  : 'This is an indicative price, not a confirmed booking quote.'}
              </p>
            )}
          </div>
        </div>

        {/* CTA — canonical brand style, matches every other primary button
            across the product (Homepage · Flights · Hotels · Packages). */}
        <div className="px-6 pb-6 pt-2 bg-white sticky bottom-0">
          <button
            data-testid="confirm-booking-btn"
            type="button"
            onClick={onConfirm}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-xl shadow-md shadow-blue-600/25 hover:shadow-lg hover:shadow-blue-600/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all min-h-[52px] text-base"
          >
            {language === 'bg' ? 'Продължи към доставчика' : 'Continue to provider'}
          </button>
          <p className="text-center text-[11px] text-gray-400 mt-3 leading-relaxed">
            {language === 'bg' ? 'Резервацията и плащането се извършват на сайта на партньора.' : 'Booking and payment take place on the partner website.'}
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-gray-600">
        {icon}
        {label}
      </span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}
